import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import Loader from "../Common/Loader";
import { useLocation } from "react-router-dom";
// Import your Redux actions for admin users
import {
  getAdminUsersAction,
  deleteAdminUserAction,
} from "../../../store/actions/AdminUserAction";
import { handleSuccess, showErrorAlert } from "../../../services/ErrorHandlingService";

const Users = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Grab users list from Redux state
  const users = useSelector((state) => {
    return state.adminUser.adminUsers || [];
  });

  // Get total number of rows from Redux state (similar to Medicine component)
  const numRows = useSelector((state) => state.adminUser.num_rows || 0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // For search/filter (use actual keys)
  const [searchQueries, setSearchQueries] = useState({
    admin_username: "",
    admin_email: "",
    admin_status: "",
    role_name: "",
  });

  const [sortField, setSortField] = useState("admin_username");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = 10;

  // Track active search for server-side filtering
  const [activeSearchField, setActiveSearchField] = useState("");
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("");

  // Fetch users on component mount
  const location = useLocation();

  useEffect(() => {
    const fetchAdminUsers = async () => {
      try {
        setLoading(true);
        setError("");

        if (location.state?.currentPage !== undefined) {
          setCurrentPage(location.state.currentPage);
        }
        if (location.state?.searchQueries) {
          setSearchQueries(location.state.searchQueries);
        }
        if (location.state?.sortField) {
          setSortField(location.state.sortField);
        }
        if (location.state?.sortOrder) {
          setSortOrder(location.state.sortOrder);
        }

        await fetchAdminUsersFromServer();
      } catch (error) {
        console.error("Error fetching admin users:", error);
        setError("Failed to load admin users. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchAdminUsers();
  }, [dispatch]);

  const fetchAdminUsersFromServer = async (
    search_by = "",
    search_keyword = "",
    sort_by = sortField,
    sort_order = sortOrder,
    pagination = true,
    pageid = 0
  ) => {
    try {
      await dispatch(
        getAdminUsersAction(pagination, pageid, search_by, search_keyword, sort_by, sort_order)
      );
    } catch (error) {
      console.error("Error fetching admin users:", error);
      setError("Failed to load admin users.");
    }
  };

  const handleSearch = (e, field) => {
    const keyword = e.target.value;
    
    // Clear all search queries first, then set the current field
    const clearedQueries = {
      admin_username: "",
      admin_email: "",
      admin_status: "",
      role_name: "",
    };
    
    const updatedQueries = { ...clearedQueries, [field]: keyword };
    setSearchQueries(updatedQueries);
    setCurrentPage(0); // Reset to first page on search

    // Track active search
    setActiveSearchField(field);
    setActiveSearchKeyword(keyword);
    fetchAdminUsersFromServer(field, keyword, sortField, sortOrder, true, 0);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchAdminUsersFromServer(
      activeSearchField,
      activeSearchKeyword,
      sortField,
      sortOrder,
      true,
      page
    );
  };

  // Handle sorting by field
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
    fetchAdminUsersFromServer(activeSearchField, activeSearchKeyword, field, order, true, currentPage);
  };

  // Navigate to edit user page
  const handleEdit = (user) => {
    if (user.role_name === "Doctor") {
      swal("Not Allowed", "Editing Doctor user is not permitted.", "warning");
      return;
    }

    navigate(`/edit-user/${user.admin_id}`, {
      state: {
        user,
        returnTo: {
          currentPage,
          searchQueries,
          sortField,
          sortOrder,
        },
      },
    });
  };

  // Confirm and delete user
  const handleDelete = (user) => {
    swal({
      title: "Are you sure?",
      text: `Delete user "${user.admin_username}"? This action cannot be undone!`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        const admin_id = user.admin_id || user.id || user._id;

        if (!admin_id) {
          showErrorAlert("Invalid user ID", "error");
          return;
        }

        try {
          setLoading(true);
          await dispatch(deleteAdminUserAction({ admin_id }));
          handleSuccess("User has been deleted successfully!");

          // Refresh the current page after deletion
          fetchAdminUsersFromServer(
            activeSearchField,
            activeSearchKeyword,
            sortField,
            sortOrder,
            true,
            currentPage
          );
        } catch (err) {
          console.error("Delete failed:", err);
          showErrorAlert("Failed to delete user. Please try again.", "error");
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Server already returns paginated data
  const paginatedData = users;
  const totalPages = Math.ceil(numRows / rowsPerPage);

  return (
    <>
      <div className="row mb-3">
        <div className="col-12 text-end">
          <button
            className="btn btn-primary btn-rounded"
            onClick={() =>
              navigate("/add-user", {
                state: {
                  returnTo: {
                    currentPage,
                    searchQueries,
                    sortField,
                    sortOrder,
                  },
                },
              })
            }
          >
            + Add User
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {error && <div className="text-danger mb-2">{error}</div>}
              {loading ? (
                <div className="text-center my-5">
                  <Loader size="lg" />
                </div>
              ) : (
                <>
                  {/* Responsive Table Wrapper */}
                  <div className="table-responsive">
                    <table className="table table-striped mb-4">
                      <thead className="table-light">
                        <tr>
                          <th onClick={() => handleSort("admin_username")} style={{ cursor: "pointer" }}>
                            User Name{" "}
                            {sortField === "admin_username" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.admin_username}
                              onChange={(e) => handleSearch(e, "admin_username")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search User"
                            />
                          </th>
                          <th onClick={() => handleSort("role_name")} style={{ cursor: "pointer" }}>
                            Role{" "}
                            {sortField === "role_name" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.role_name || ""}
                              onChange={(e) => handleSearch(e, "role_name")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Role"
                            />
                          </th>
                          <th onClick={() => handleSort("admin_email")} style={{ cursor: "pointer" }}>
                            Email{" "}
                            {sortField === "admin_email" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.admin_email}
                              onChange={(e) => handleSearch(e, "admin_email")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Email"
                            />
                          </th>
                          <th onClick={() => handleSort("admin_status")} style={{ cursor: "pointer" }}>
                            Status{" "}
                            {sortField === "admin_status" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.admin_status}
                              onChange={(e) => handleSearch(e, "admin_status")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Status"
                            />
                          </th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.map((user) => (
                          <tr key={user.admin_id}>
                            <td>{user.admin_username}</td>
                            <td>{user.role_name || "-"}</td>
                            <td>{user.admin_email}</td>
                            <td>
                              <span
                                className={`badge px-3 py-1 rounded-pill text-white ${
                                  user.admin_status.toLowerCase() === "active"
                                    ? "bg-success"
                                    : "bg-danger"
                                }`}
                              >
                                {user.admin_status}
                              </span>
                            </td>
                            <td className="align-middle">
                              <div className="d-flex ">
                                <span
                                  className="edit me-3"
                                  onClick={() => handleEdit(user)}
                                  style={{
                                    cursor: user.role_name === "Doctor" ? "not-allowed" : "pointer",
                                    opacity: user.role_name === "Doctor" ? 0.5 : 1,
                                  }}
                                  title={
                                    user.role_name === "Doctor"
                                      ? "Editing Doctor user is not allowed"
                                      : "Edit user"
                                  }
                                >
                                  <i className="fa fa-pencil-alt text-success"></i>
                                </span>
                                <span
                                  className="delete me-2"
                                  onClick={() => {
                                    if (user.role_name === "Doctor") {
                                      swal(
                                        "Not Allowed",
                                        "Deleting Doctor user is not permitted.",
                                        "warning"
                                      );
                                      return;
                                    }
                                    handleDelete(user);
                                  }}
                                  style={{
                                    cursor: user.role_name === "Doctor" ? "not-allowed" : "pointer",
                                    opacity: user.role_name === "Doctor" ? 0.5 : 1,
                                  }}
                                  title={
                                    user.role_name === "Doctor"
                                      ? "Deleting Doctor user is not allowed"
                                      : "Delete user"
                                  }
                                >
                                  <i className="fa fa-trash text-danger"></i>
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {paginatedData.length === 0 && (
                          <tr>
                            <td colSpan="5" className="text-center">
                              No users found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div
                    className="pagination-wrapper"
                    style={{
                      display: "flex",
                      gap: "6px",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: "20px",
                      flexWrap: "wrap",
                      flexDirection: "column",
                    }}
                  >
                    <div>
                      Showing {users?.length === 0 ? 0 : currentPage * rowsPerPage + 1} to{" "}
                      {Math.min((currentPage + 1) * rowsPerPage, numRows)} of {numRows} entries
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "6px",
                        flexWrap: "wrap",
                        justifyContent: "center",
                      }}
                    >
                      <button
                        // onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                        onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                        disabled={currentPage === 0}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: currentPage === 0 ? "not-allowed" : "pointer",
                          opacity: currentPage === 0 ? 0.5 : 1,
                        }}
                      >
                        &lt;
                      </button>
                      {(() => {
                        const pages = [];
                        const maxPagesToShow = 5;
                        const startPage = Math.max(
                          0,
                          Math.min(currentPage - 2, totalPages - maxPagesToShow)
                        );
                        const endPage = Math.min(startPage + maxPagesToShow, totalPages);

                        if (startPage > 0) {
                          pages.push(
                            <button
                              key={0}
                              onClick={() => handlePageChange(0)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                color: "#000",
                                cursor: "pointer",
                              }}
                            >
                              1
                            </button>
                          );
                          if (startPage > 1) {
                            pages.push(
                              <span
                                key="dots1"
                                style={{ padding: "6px 12px", userSelect: "none" }}
                              >
                                ...
                              </span>
                            );
                          }
                        }

                        for (let i = startPage; i < endPage; i++) {
                          const isActive = currentPage === i;
                          pages.push(
                            <button
                              key={i}
                              onClick={() => handlePageChange(i)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: isActive ? "#007bff" : "#fff",
                                color: isActive ? "#fff" : "#000",
                                cursor: "pointer",
                              }}
                            >
                              {i + 1}
                            </button>
                          );
                        }

                        if (endPage < totalPages) {
                          if (endPage < totalPages - 1) {
                            pages.push(
                              <span
                                key="dots2"
                                style={{ padding: "6px 12px", userSelect: "none" }}
                              >
                                ...
                              </span>
                            );
                          }
                          pages.push(
                            <button
                              key={totalPages - 1}
                              onClick={() => handlePageChange(totalPages - 1)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                color: "#000",
                                cursor: "pointer",
                              }}
                            >
                              {totalPages}
                            </button>
                          );
                        }

                        return pages;
                      })()}
                      <button
                        // onClick={() =>
                        //   setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))
                        // }
                        onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                        disabled={currentPage === totalPages - 1}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                          opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                        }}
                      >
                        &gt;
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Users;
